import bulmaSteps from "bulma-steps/dist/js/bulma-steps";
import pdfWorker from "pdfjs-dist/legacy/build/pdf.worker.entry";
import * as pdfjs from "pdfjs-dist/legacy/build/pdf";
import React, { useContext, useEffect, useMemo, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import { scrollTop, smoothScroll } from "../../../utils/scrollbarUtil";
import Step from "../submission/steps/Step";
import Steps, { STEPS, UZOP_STEPS } from "../submission/steps/Steps";
import Info from "./info/Info";
import PdfForm from "./pdf/PdfForm";
import "./RequestSubmission.scss";
import { RequestSubmissionContext } from "./RequestSubmissionContext";
import { init, initializePdfFormStateObject, reducer } from "./RequestSubmissionReducer";
import Review from "./review/Review";
import DocumentUpload from "./upload/DocumentUpload";
import { GET, POST } from "../../../utils/fetchUtil";
import Content from "../../../common/content/Content";
import Loader from "../../../common/loader/Loader";
import { MessageContext } from "../../../common/message";
import { UserContext } from "../../../common/user/UserContext";
import { Prompt, useLocation } from "react-router-dom";

// TODO Move to some other place?
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;

function RequestSubmission({ id, uid, title }) {
  const { t } = useTranslation();
  const message = useContext(MessageContext);
  const location = useLocation();
  const [preLoc, setPreLoc] = useState({});
  const user = useContext(UserContext);

  const [state, dispatch] = useReducer(
    reducer,
    {
      id: id,
      uid: null, // wait for fetch to set uid (serves as an indicator that state is ready)
      isBlocking: false,
      newReq: false,
      activeStepIndex: 0,
      info: {},
      pdfForm: initializePdfFormStateObject(null, null, null, 1.5),
      upload: {},
      paymentAndDelivery: {},
      review: {},
    },
    init
  );

  const info = useMemo(() => <Info />, []);
  const review = useMemo(() => <Review />, []);

  useEffect(() => {
    bulmaSteps.attach();

    const initialize = (type, newRequestSubmission) => {
      dispatch({
        type: type,
        payload: newRequestSubmission,
      });
    };

    setPreLoc(location);

    uid
      ? GET(
          `/requests/${id}/submissions/${uid}`,
          (response) => initialize("load", response),
          () => {}
          /* (error) => {
            message.toggle({
              title: t("requests.reviewDocuments.action.save.error"),
              content: error,
              hideNo: true,
              yesLabel: t("modalMessage.okLabel"),
              onYes: () => {
                message.toggle({});
              },
            });
          } */
        )
      : POST(
          `/requests/${id}/submissions`,
          location.state !== null ? { institutionId: location.state } : null,
          (response) => {
            initialize("init", response);
            if (response.organizationUnits.length === 1) {
              initialize("setOrgUnitId", parseInt(response.organizationUnits[0].label));
            }
          },
          () => {}
        );
  }, [id, uid]);

  // Scroll to top when step changes
  useEffect(() => {
    scrollTop(".react-pdf__Document");
    scrollTop("#reqsub");
    smoothScroll("#root");
  }, [state.activeStepIndex]);

  if (state.uid) {
    return (
      <RequestSubmissionContext.Provider value={{ state, dispatch }}>
        <Content id="reqsub" isContainer={true} isScrollable={false}>
          <Prompt
            when={state.isBlocking === true}
            message={
              state.newReq === true
                ? t("requests.submission.actions.quitMessage")
                : t("requests.submission.actions.quitMessageSavedReq")
            }
          />

          <div className="title has-text-centered p-4">{title}</div>
          {state.uid && user.place !== "UZOP" && user.place !== "eHKMS Info" ? (
            <Steps>
              <Step
                label={t("requests.submission.steps.info")}
                isActive={state.activeStepIndex === STEPS.INFO}
                render={() => info}
              />
              <Step
                label={t("requests.submission.steps.pdfForm")}
                isActive={state.activeStepIndex === STEPS.PDF_FORM}
                render={() => (
                  <PdfForm
                    pdf={state.pdfForm.url}
                    scale={state.pdfForm.scale}
                    pdfFormStateObjectRef={state.pdfForm}
                    dispatch={dispatch}
                  />
                )}
              />
              <Step
                label={t("requests.submission.steps.upload")}
                isActive={state.activeStepIndex === STEPS.UPLOAD}
                render={() => (
                  <DocumentUpload
                    className="section"
                    dispatchUpdateAction={() => dispatch({ type: "update-documents" })}
                    documents={state.upload.documents}
                    minDocumentsAttached={state.upload.uploadConstraints.minDocumentsAttached}
                    acceptDocumentTypes={state.upload.uploadConstraints.acceptDocumentTypes}
                    maxDocumentSizeInMb={state.upload.uploadConstraints.maxDocumentSizeInMb}
                    formDataAsJson={{ id: state.uid }}
                  />
                )}
              />
              <Step
                label={t("requests.submission.steps.paymentAndDelivery")}
                isActive={state.activeStepIndex === STEPS.PAYMENT_AND_DELIVERY}
                render={() => <div>Payment & delivery</div>}
              />
              <Step
                label={t("requests.submission.steps.review")}
                isActive={state.activeStepIndex === STEPS.REVIEW}
                render={() => review}
              />
            </Steps>
          ) : (
            <Steps>
              <Step
                label={t("requests.submission.steps.info")}
                isActive={state.activeStepIndex === UZOP_STEPS.INFO}
                render={() => info}
              />
              <Step
                label={t("requests.submission.steps.pdfForm")}
                isActive={state.activeStepIndex === UZOP_STEPS.PDF_FORM}
                render={() => (
                  <PdfForm
                    pdf={state.pdfForm.url}
                    scale={state.pdfForm.scale}
                    pdfFormStateObjectRef={state.pdfForm}
                    dispatch={dispatch}
                  />
                )}
              />
              <Step
                label={t("requests.submission.steps.upload")}
                isActive={state.activeStepIndex === UZOP_STEPS.UPLOAD}
                render={() => (
                  <DocumentUpload
                    className="section"
                    dispatchUpdateAction={() => dispatch({ type: "update-documents" })}
                    documents={state.upload.documents}
                    minDocumentsAttached={state.upload.uploadConstraints.minDocumentsAttached}
                    acceptDocumentTypes={state.upload.uploadConstraints.acceptDocumentTypes}
                    maxDocumentSizeInMb={state.upload.uploadConstraints.maxDocumentSizeInMb}
                    formDataAsJson={{ id: state.uid }}
                  />
                )}
              />
              <Step
                label={t("requests.submission.steps.review")}
                isActive={state.activeStepIndex === UZOP_STEPS.REVIEW}
                render={() => review}
              />
            </Steps>
          )}
        </Content>
      </RequestSubmissionContext.Provider>
    );
  } else {
    return <Loader size={`8x`} />;
  }
}

export default RequestSubmission;
